import { useTranslation } from "react-i18next";
import {
  Calendar,
  Trash2,
  Lock,
  Users,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
  List,
  Check,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from "@tabipla/ui";
import { ScheduleMatrix } from "../ScheduleMatrix";
import type { ScheduleCardProps } from "./types";
import { AvailabilityIcon } from "./AvailabilityIcon";
import { ResponseButton } from "./ResponseButton";
import { formatDate } from "./utils";

/**
 * ScheduleCard presenter component.
 * Pure presentation - receives all data and callbacks as props.
 * Does not contain any hooks for data fetching or mutations.
 */
export function ScheduleCard({
  schedule,
  isExpanded,
  localResponses,
  viewMode,
  saveStatus,
  bestDates,
  isClosing,
  isDeleting,
  onToggleExpand,
  onClose,
  onDelete,
  onViewModeChange,
  onResponseChange,
}: ScheduleCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <CardHeader
        className="cursor-pointer p-4 transition-colors hover:bg-muted/30"
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-base font-semibold">{schedule.title}</CardTitle>
            {schedule.description && (
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                {schedule.description}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {t("schedules.respondentsCount", { count: schedule.totalRespondents })}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {t("schedules.dateCount", { count: schedule.dateOptions.length })}
              </span>
              {schedule.isClosed && (
                <Badge variant="outline" className="text-xs">
                  <Lock className="mr-1 h-3 w-3" />
                  {t("schedules.closed")}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted/50">
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-0">
          <div className="px-4 pb-4">
            {/* View Mode Toggle with Save Status */}
            <div className="mb-4 flex items-center justify-between gap-2">
              {/* Save status indicator */}
              <div className="flex items-center gap-1.5 text-xs">
                {saveStatus === "saving" && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    {t("schedules.saving")}
                  </span>
                )}
                {saveStatus === "saved" && (
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <Check className="h-3.5 w-3.5" />
                    {t("schedules.saved")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onViewModeChange("list")}
                  className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                    viewMode === "list"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted hover:bg-muted/80"
                  }`}
                >
                  <List className="h-3.5 w-3.5" />
                  {t("schedules.listView")}
                </button>
                <button
                  onClick={() => onViewModeChange("matrix")}
                  className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                    viewMode === "matrix"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted hover:bg-muted/80"
                  }`}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  {t("schedules.matrixView")}
                </button>
              </div>
            </div>

            {/* Matrix View */}
            {viewMode === "matrix" && (
              <ScheduleMatrix
                schedule={schedule}
                localResponses={localResponses}
                onResponseChange={onResponseChange}
              />
            )}

            {/* List View (Original Table) */}
            {viewMode === "list" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="py-2 text-left font-medium">{t("schedules.date")}</th>
                      <th className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <AvailabilityIcon status="available" className="h-4 w-4" />
                        </div>
                      </th>
                      <th className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <AvailabilityIcon status="maybe" className="h-4 w-4" />
                        </div>
                      </th>
                      <th className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <AvailabilityIcon status="unavailable" className="h-4 w-4" />
                        </div>
                      </th>
                      {!schedule.isClosed && (
                        <th className="py-2 text-right">{t("schedules.yourResponse")}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.dateSummaries.map((dateSummary) => {
                      const isBestDate =
                        bestDates.length > 0 && bestDates[0].date === dateSummary.date;
                      return (
                        <tr
                          key={dateSummary.date}
                          className={`border-b last:border-0 ${isBestDate ? "bg-green-50 dark:bg-green-950/20" : ""}`}
                        >
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{formatDate(dateSummary.date)}</span>
                              {isBestDate && (
                                <Badge variant="default" className="bg-green-500 text-xs">
                                  {t("schedules.bestDate")}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-3 text-center">
                            <span className="font-medium text-green-600">
                              {dateSummary.available}
                            </span>
                          </td>
                          <td className="px-2 py-3 text-center">
                            <span className="font-medium text-yellow-600">{dateSummary.maybe}</span>
                          </td>
                          <td className="px-2 py-3 text-center">
                            <span className="font-medium text-red-600">
                              {dateSummary.unavailable}
                            </span>
                          </td>
                          {!schedule.isClosed && (
                            <td className="py-3">
                              <div className="flex justify-end gap-1">
                                <ResponseButton
                                  status="available"
                                  isSelected={localResponses[dateSummary.date] === "available"}
                                  onClick={() => onResponseChange(dateSummary.date, "available")}
                                />
                                <ResponseButton
                                  status="maybe"
                                  isSelected={localResponses[dateSummary.date] === "maybe"}
                                  onClick={() => onResponseChange(dateSummary.date, "maybe")}
                                />
                                <ResponseButton
                                  status="unavailable"
                                  isSelected={localResponses[dateSummary.date] === "unavailable"}
                                  onClick={() => onResponseChange(dateSummary.date, "unavailable")}
                                />
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Respondents Details */}
            <div className="mt-4 space-y-2 rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center justify-between">
                <h4 className="flex items-center gap-1.5 text-sm font-medium">
                  <Check className="h-4 w-4 text-green-500" />
                  {t("schedules.respondedMembers")}
                </h4>
                <span className="text-xs text-muted-foreground">
                  {t("schedules.respondentsCount", { count: schedule.totalRespondents })}
                </span>
              </div>
              {schedule.userResponses.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {schedule.userResponses.map((userResponse) => (
                    <Badge key={userResponse.userId} variant="secondary" className="gap-1 text-xs">
                      <Check className="h-3 w-3 text-green-500" />
                      {userResponse.userName}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("schedules.noRespondentsYet")}</p>
              )}
              {!schedule.isClosed && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t("schedules.waitingForOthers")}
                </p>
              )}
            </div>

            {/* Actions */}
            {!schedule.isClosed && (
              <div className="mt-4 flex gap-2 border-t pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                  disabled={isClosing}
                  className="flex-1 rounded-xl"
                >
                  <Lock className="mr-2 h-4 w-4" />
                  {t("schedules.closeSchedule")}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="h-9 w-9 rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            {schedule.isClosed && (
              <div className="mt-4 flex gap-2 border-t pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("common.delete")}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
